// Scoring + routing engine for the funnel survey.
//
// Scores are HIDDEN from the respondent. They are stored per question so lead
// quality can be tracked per landing page in the admin.
//
// Questions:
//   Q1 clinic_owner, Q2 offers_treatment, Q3 care_plan_price
//
// Evaluation order (stop at the first match):
//   1. DISQUALIFIED  -> DQ page       (Q1 = B/C, or Q3 = "Less than $1,500")
//   2. QUALIFIED (3) -> Demo calendar (Q2 = A/B AND Q3 = $3,000+ band)
//   3. MAYBE (2)     -> Intro calendar (everything else)

import { OUTCOMES, SURVEY_STEPS, type SurveyAnswers, type SurveyOutcome } from "./survey-config";

export type SurveyTier = "3" | "2" | "DQ";
export type SurveyRoutingOutcome = "demo" | "intro" | "disqualified";

export type SurveyRouting = {
  tier: SurveyTier;
  outcome: SurveyRoutingOutcome;
  outcomeId: string;
  /** Per-question hidden score, keyed by question id. */
  scores: Record<string, number>;
  totalScore: number;
  dqReason: string | null;
  /** True once the routing decision is final (DQ, or all of Q1–Q3 answered). */
  decided: boolean;
};

const Q1 = "clinic_owner";
const Q2 = "offers_treatment";

const NOT_OWNER = [
  "No - I'm going to leave this page",
  "I'm a patient looking for a doctor",
];
/** Q2 answers that can reach a demo (pending the price band). */
const DEMO_ELIGIBLE_TREATMENT = [
  "Yes, I offer it now and have the equipment for it",
  "I have the equipment but I'm not actively running it now",
];
/** Q3 bands worth $3,000+. */
const PRICE_DEMO_BANDS = ["$10,000+", "$5,000 to $10,000", "$3,000 to $5,000"];
const PRICE_DQ = "Less than $1,500";

const asString = (v: unknown) => (typeof v === "string" ? v : "");

/** Single merged care-plan price question. */
export function carePlanAnswer(answers: SurveyAnswers): string {
  return asString(answers.care_plan_price);
}

/** Hidden score for every answered choice question. */
export function scoreAnswers(answers: SurveyAnswers): Record<string, number> {
  const scores: Record<string, number> = {};
  for (const step of SURVEY_STEPS) {
    if (step.kind !== "choice" && step.kind !== "multi_choice") continue;
    const raw = answers[step.id];
    const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
    if (values.length === 0) continue;
    const best = values
      .map((v) => step.options.find((o) => o.value === v)?.score)
      .filter((s): s is number => typeof s === "number");
    if (best.length === 0) continue;
    scores[step.id] = Math.max(...best);
  }
  return scores;
}

export function evaluateSurveyRouting(answers: SurveyAnswers): SurveyRouting {
  const scores = scoreAnswers(answers);
  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const q1 = asString(answers[Q1]);
  const q2 = asString(answers[Q2]);
  const q3 = carePlanAnswer(answers);

  const dq = (reason: string): SurveyRouting => ({
    tier: "DQ",
    outcome: "disqualified",
    outcomeId: "dq_modal",
    scores,
    totalScore,
    dqReason: reason,
    decided: true,
  });

  // 1. Disqualification — first match wins.
  if (q1 && NOT_OWNER.includes(q1)) return dq("Not a clinic owner (Q1)");
  if (q3 === PRICE_DQ) return dq("Care plan under $1,500 (Q3)");

  const decided = Boolean(q1 && q2 && q3);

  // 2. Qualified (Tier 3) — demo-eligible treatment AND $3,000+ care plan.
  if (DEMO_ELIGIBLE_TREATMENT.includes(q2) && PRICE_DEMO_BANDS.includes(q3)) {
    return {
      tier: "3",
      outcome: "demo",
      outcomeId: "demo_call",
      scores,
      totalScore,
      dqReason: null,
      decided,
    };
  }

  // 3. Maybe (Tier 2) — everything else (the lower signal wins).
  return {
    tier: "2",
    outcome: "intro",
    outcomeId: "discovery_call",
    scores,
    totalScore,
    dqReason: null,
    decided,
  };
}

export function routingOutcomeConfig(routing: SurveyRouting): SurveyOutcome {
  return OUTCOMES[routing.outcomeId] ?? OUTCOMES.discovery_call;
}

export const TIER_LABEL: Record<SurveyTier, string> = {
  "3": "Qualified (Tier 3)",
  "2": "Maybe (Tier 2)",
  DQ: "Disqualified",
};

export const OUTCOME_LABEL: Record<SurveyRoutingOutcome, string> = {
  demo: "Demo call",
  intro: "Intro call",
  disqualified: "Disqualified",
};
